import React, { useEffect } from 'react';
import {
  Form,
  FormInstance,
  DateSerialized,
  SelectInput,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectLanguage from '../language';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import { CardBlock, CardBreakout } from '../../card';
import Callout from '../../callout';
import {
  GetCurrentTZ,
  GetFormalDate,
  ShorthandDate
} from '../../../../libs/time';
import { useMetaData } from '../../../../contexts/metadata';

type Mode = 'B' | 'D' | 'T' | 'C';

interface EpxHyperData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  isFixed: boolean;
  mode?: Mode;
  times: string[];
  days: number[];
  hasMedInstruction?: boolean;
  hasFeedback?: boolean;
  shouldAskMeds?: boolean;
  shouldAskHeartRate?: boolean;
}

type EpxHyperDataRaw = DateSerialized<EpxHyperData, 'start' | 'end'>;

const INITIAL_TIMES = ['09:00', '14:00'];

export const EpxHyperDefaultData: EpxHyperData = {
  method: 'message',
  language: 'en',
  start: GetFormalDate(ShorthandDate.Today),
  end: null,
  tz: GetCurrentTZ(),
  isFixed: false,
  mode: 'B',
  times: INITIAL_TIMES,
  days: [1, 4],
  hasMedInstruction: false,
  hasFeedback: true,
  shouldAskMeds: true,
  shouldAskHeartRate: false
};

const DATE_FORMAT = 'YYYY-MM-DD';
const { DeserializeDate, SerializeDate } = FormInstance;

const Modes = [
  {
    label: '1 Reminder 2 Days per Week',
    value: 'B'
  },
  {
    label: '1 Reminder per Day',
    value: 'D'
  },
  {
    label: '2 Reminders per Day',
    value: 'T'
  },
  {
    label: 'Custom Days',
    value: 'C'
  }
];

const GetNTimesFromMode = (mode: Mode): number => {
  let nTimes = 1;
  switch (mode) {
    case 'B':
    case 'D':
    case 'C':
      nTimes = 1;
      break;
    case 'T':
      nTimes = 2;
      break;
  }
  return nTimes;
};

interface EpxHyperFormProps {
  setForm?: (form: FormInstance<EpxHyperDataRaw>) => void;
  onSubmit?: (data: EpxHyperData) => void;
  isDisabled?: boolean;
}

const EpxHyperForm = ({
  setForm,
  onSubmit,
  isDisabled
}: EpxHyperFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const serialize = (data: EpxHyperDataRaw): EpxHyperData => {
    return {
      ...data,
      start: SerializeDate(data.start, DATE_FORMAT),
      end: SerializeDate(data.end, DATE_FORMAT),
      times: data.times.slice(0, GetNTimesFromMode(data.mode))
    };
  };
  const deserialize = (data: EpxHyperData): EpxHyperDataRaw => {
    return {
      ...data,
      start: DeserializeDate(data.start, DATE_FORMAT),
      end: DeserializeDate(data.end, DATE_FORMAT)
    };
  };
  const defaultData = deserialize(EpxHyperDefaultData);
  const form = new FormInstance<EpxHyperDataRaw, EpxHyperData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onChange = (data: EpxHyperData): void => {
    if (!data.isFixed && data.mode !== 'B') {
      data.mode = 'B';
      form.setData(data);
    }
  };
  return (
    <CardBlock bare>
      <Form
        form={form}
        onChange={onChange}
        onSubmit={onSubmit}
        disabled={isDisabled}
      >
        {(props) => {
          const isFixed = props.values.isFixed;
          const mode: Mode = props.values.mode;
          const nTimes = GetNTimesFromMode(mode);
          const timeCols = {
            sm: nTimes > 2 ? 24 : Math.min(24 / nTimes, 12),
            md: Math.min(24 / nTimes, 12)
          };
          let nDays = null;
          if (mode === 'B') {
            nDays = 2;
          }
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <SelectLanguage />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <StartDate label="Start Date" name="start" fill />
                  </Col>
                  <Col sm={12}>
                    <EndDate label="End Date" name="end" />
                  </Col>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Schedule Type"
                      name="isFixed"
                      trueLabel="Fixed Schedule"
                      falseLabel="SMART Schedule"
                      fill
                    />
                  </Col>
                  {isFixed ? (
                    <Col sm={12}>
                      <SelectInput
                        label="Reminder Frequency"
                        name="mode"
                        options={Modes}
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
              </CardBlock>
              {['B', 'C', 'T'].includes(mode) ? (
                <CardBreakout>
                  <Weekday
                    name="days"
                    numItems={nDays}
                    minItems={nDays === null ? 1 : null}
                    inline
                  />
                </CardBreakout>
              ) : null}
              <CardBlock>
                <Row>
                  <Col {...timeCols}>
                    <SelectTime
                      label={nTimes === 1 ? 'Reminder At' : '1st Reminder'}
                      name="times[0]"
                      fill
                    />
                  </Col>
                  {['T'].includes(mode) ? (
                    <Col {...timeCols}>
                      <SelectTime
                        label="2nd Reminder"
                        name="times[1]"
                        defaultValue={INITIAL_TIMES[1]}
                        fill
                      />
                    </Col>
                  ) : null}
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Give Feedback"
                      name="hasFeedback"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask Heart Rate Question"
                      name="shouldAskHeartRate"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Ask Medication Question"
                      name="shouldAskMeds"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be asked if they
                      have taken their blood pressure medications for the day if
                      they report a high blood pressure.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Give Medication Instruction"
                      name="hasMedInstruction"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                      disabled={!props.values.shouldAskMeds}
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be instructed to
                      take their blood pressure medications as prescribed by
                      their doctor and recheck and report their blood pressure
                      again.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxHyperForm;
